import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import { formatUuidT } from "../../flatbuffers/display";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

/**
 * Control-message write routing rules for merged channels.
 *
 * A MERGE/MIXED channel aggregates readings from several endpoints, and
 * `merge_policy` reconciles them. Writes had no equivalent: a Control command is
 * addressed to a channel, so on a merged channel with several live endpoints
 * nothing said which one the write should reach. A rule names one Control value
 * and the ordered endpoints it may be written to.
 */
export const controlRouteAPI = createApi({
  reducerPath: "controlRouteAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetControlRoutes"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    /** GET /api/control_routes/[?channel_id=uuid] */
    getControlRoutes: builder.query({
      query: (body: { token: string; channelId?: string }) => ({
        url: `/control_routes/${body.channelId ? `?channel_id=${body.channelId}` : ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetControlRoutes"],
    }),

    /** GET /api/control_routes/:id */
    getControlRoute: builder.query({
      query: (body: { id: string; token: string }) => ({
        url: `/control_routes/${body.id}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetControlRoutes"],
    }),

    /** POST /api/control_routes/ */
    postControlRoute: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: "/control_routes/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetControlRoutes"],
    }),

    /**
     * PUT /api/control_routes/:id
     *
     * The target list is replaced wholesale, not merged: omitting a target
     * means "this rule no longer writes there".
     */
    putControlRoute: builder.mutation({
      query: (body: { body: Record<string, unknown>; token: string }) => ({
        url: `/control_routes/${formatUuidT(body.body.id as string)}`,
        method: "PUT",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetControlRoutes"],
    }),

    /** DELETE /api/control_routes/:id */
    deleteControlRoute: builder.mutation({
      query: (body: { id: string; token: string }) => ({
        url: `/control_routes/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetControlRoutes"],
    }),
  }),
});

export const {
  useLazyGetControlRoutesQuery,
  useLazyGetControlRouteQuery,
  usePostControlRouteMutation,
  usePutControlRouteMutation,
  useDeleteControlRouteMutation,
} = controlRouteAPI;
